import React, { useEffect } from "react";
import { X } from "lucide-react";

interface GlassModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
  /** Optional ref forwarded onto the scrollable body div, so callers can programmatically scroll it */
  bodyRef?: React.RefObject<HTMLDivElement>;
}

export const GlassModal: React.FC<GlassModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = "max-w-2xl",
  bodyRef
}) => {
  // Handle background scrolling AND Escape key dismissal
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "unset";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 pt-2 sm:pt-0 overflow-x-hidden overflow-y-auto">
      {/* Backdrop Layer */}
      <div
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Container */}
      <div className={`relative w-full ${maxWidth} bg-white border border-slate-200/80 rounded-2xl shadow-[0_24px_50px_-12px_rgba(15,23,42,0.18)] flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200 z-10`}>

        {/* Header */}
        <div className="px-8 pt-7 flex items-center justify-between shrink-0 border-b border-slate-100">
          {title && (
            <div className="space-y-1">
              <h3 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h3>
              <div className="h-0.5 w-7 bg-[#8dc63f] rounded-full" />
            </div>
          )}
          <button
            onClick={onClose}
            className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-lg border border-slate-200/60 transition-all active:scale-95 group"
          >
            <X className="w-4 h-4 transition-transform group-hover:rotate-90 duration-150" />
          </button>
        </div>

        {/* Main Body */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-8 py-4 text-slate-600 bg-white/40">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-8 py-4 flex items-center justify-end rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};